import {
	mappedMasksAtom,
	selectedMaskAtom,
	selectedMaskGroupAtom,
	updateMask,
	updateMaskGroup,
	showingFloatingMaskingPanelAtom,
	focusedMaskAdjustmentAtom,
} from "@/store/editor/masks-state"
import { useTranslation } from "react-i18next"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { noopSliderTransform, Slider, sliderBackgrounds } from "@/components/ui/Slider"
import {
	BackgroundMask,
	GradientMask,
	MaskGroup,
	Mask,
	PeopleMask,
	RadialMask,
	SkyMask,
	SubjectMask,
	BrushMask,
} from "@/core/types"
import {
	activeAdjustmentsAtom,
	currentAdjustmentsAtom,
	saveCurrentAdjustmentState,
} from "@/store/store-history"
import { Button } from "@/components/ui/Button"
import { Icon } from "@/components/icons/Icon"
import { currentCursorPresetNameAtom, cursorAtom } from "@/store/brush-cursor-state"
import { Switch } from "../ui/Switch"
import { HSLPointColor } from "../HSL/HSLPointColor"
import { isColorProfileMonochrome } from "@/core/utils/camera-raw-settings"
import { useShortcuts } from "@/utils/useShortcuts"
import { useEditor } from "@/utils/useEditor"
import { useEffect } from "react"
import {
	MaskAdjustmentFocusKey,
	brushMaskFocusKeys,
	objectMaskFocusKeys,
	radialMaskFocusKeys,
} from "../../core/adjustments"
import { actionShortcuts } from "@/shortcuts/shortcuts"
import { isCurrentPhotoSourceMonochromeAtom } from "@/store/editor/photo-loading"

const useFocusMaskAdjustments = () => {
	const selectedMask = useAtomValue(selectedMaskAtom)
	const [focusedMaskAdjustment, setFocusedMaskAdjustment] = useAtom(focusedMaskAdjustmentAtom)

	if (!selectedMask) {
		return {
			focusedMaskAdjustment: null,
			focusNextMaskAdjustment: () => null,
			focusPreviousMaskAdjustment: () => null,
		}
	}

	const maskAdjustmentKeyMap: {
		radial: readonly MaskAdjustmentFocusKey[]
		gradient: readonly MaskAdjustmentFocusKey[]
		subject: readonly MaskAdjustmentFocusKey[]
		sky: readonly MaskAdjustmentFocusKey[]
		background: readonly MaskAdjustmentFocusKey[]
		people: readonly MaskAdjustmentFocusKey[]
		brush: readonly MaskAdjustmentFocusKey[]
	} = {
		radial: radialMaskFocusKeys,
		gradient: objectMaskFocusKeys,
		subject: objectMaskFocusKeys,
		sky: objectMaskFocusKeys,
		background: objectMaskFocusKeys,
		people: objectMaskFocusKeys,
		brush: brushMaskFocusKeys,
	}

	const maskAdjustmentKeys = maskAdjustmentKeyMap[selectedMask.properties.type]

	const currentAdjustmentIndex = focusedMaskAdjustment
		? maskAdjustmentKeys.indexOf(focusedMaskAdjustment)
		: 0

	const focusNextMaskAdjustment = () => {
		const nextIndex =
			currentAdjustmentIndex < maskAdjustmentKeys.length - 1 ? currentAdjustmentIndex + 1 : 0
		setFocusedMaskAdjustment(maskAdjustmentKeys[nextIndex] || null)
	}

	const focusPreviousMaskAdjustment = () => {
		const nextIndex =
			currentAdjustmentIndex > 0 ? currentAdjustmentIndex - 1 : maskAdjustmentKeys.length - 1
		setFocusedMaskAdjustment(maskAdjustmentKeys[nextIndex] || null)
	}

	return {
		focusedMaskAdjustment,
		focusNextMaskAdjustment,
		focusPreviousMaskAdjustment,
	}
}

export const MaskAdjustmentsPanel = () => {
	const { t } = useTranslation("components")
	useEditor()
	const globalAdjustments = useAtomValue(currentAdjustmentsAtom)
	const isSourceMonochrome = useAtomValue(isCurrentPhotoSourceMonochromeAtom)
	const maskGroups = useAtomValue(mappedMasksAtom)
	const selectedGroup = useAtomValue(selectedMaskGroupAtom)
	const setShowingFloatingMaskingPanel = useSetAtom(showingFloatingMaskingPanelAtom)
	const { focusedMaskAdjustment } = useFocusMaskAdjustments()

	if (!selectedGroup) {
		return (
			<div className="px-3 py-6 text-center text-sm text-base-400">
				{maskGroups.length > 0
					? t("maskAdjustmentsPanel.noMaskSelected")
					: t("maskAdjustmentsPanel.noMaskCreated")}
			</div>
		)
	}

	const isMonochromeProfile = isColorProfileMonochrome(globalAdjustments.colorProfile)
	const disablesChromaControls = isSourceMonochrome || isMonochromeProfile
	const adjustments = selectedGroup.properties

	const handleUpdate = (name: keyof MaskGroup, val: number) => {
		updateMaskGroup(selectedGroup, { [name]: val, active: true })
	}

	const handleUpdateEnded = (name: string, val?: number | string) => {
		void saveCurrentAdjustmentState({
			message: `${adjustments.name}: ${name}`,
			changedValue: val?.toString(),
		})
	}

	return (
		<div
			aria-label={t("maskAdjustmentsPanel.ariaMaskingAdjustmentsPanel")}
			className="m-0 bg-base-800 p-2 dark:bg-base-700/50"
		>
			<MaskSettingsPanel
				onSliderDown={() => setShowingFloatingMaskingPanel(false)}
				onSliderUp={() => setShowingFloatingMaskingPanel(true)}
			/>
			<Slider
				title={t("maskAdjustmentsPanel.sliderExposure")}
				value={adjustments["exposure"]}
				transform={noopSliderTransform}
				step={0.05}
				keyboardStep={0.1}
				shiftKeyboardStep={0.33}
				min={-5}
				max={5}
				inFocus={focusedMaskAdjustment === "exposure"}
				onMouseDown={() => setShowingFloatingMaskingPanel(false)}
				onMouseUp={() => setShowingFloatingMaskingPanel(true)}
				onUpdate={(val) => handleUpdate("exposure", val)}
				onUpdateEnded={(_, displayedValue) => handleUpdateEnded("Exposure", displayedValue)}
			/>
			<Slider
				title={t("maskAdjustmentsPanel.sliderContrast")}
				background={sliderBackgrounds.contrast}
				value={adjustments["contrast"]}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "contrast"}
				onMouseDown={() => setShowingFloatingMaskingPanel(false)}
				onMouseUp={() => setShowingFloatingMaskingPanel(true)}
				onUpdate={(val) => handleUpdate("contrast", val)}
				onUpdateEnded={(_, displayedValue) => handleUpdateEnded("Contrast", displayedValue)}
			/>
			<Slider
				title={t("maskAdjustmentsPanel.sliderHighlights")}
				value={adjustments["highlights"]}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "highlights"}
				onMouseDown={() => setShowingFloatingMaskingPanel(false)}
				onMouseUp={() => setShowingFloatingMaskingPanel(true)}
				onUpdate={(val) => handleUpdate("highlights", val)}
				onUpdateEnded={(_, displayedValue) =>
					handleUpdateEnded("Highlights", displayedValue)
				}
			/>
			<Slider
				title={t("maskAdjustmentsPanel.sliderShadows")}
				value={adjustments["shadows"]}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "shadows"}
				onMouseDown={() => setShowingFloatingMaskingPanel(false)}
				onMouseUp={() => setShowingFloatingMaskingPanel(true)}
				onUpdate={(val) => handleUpdate("shadows", val)}
				onUpdateEnded={(_, displayedValue) => handleUpdateEnded("Shadows", displayedValue)}
			/>
			<Slider
				title={t("maskAdjustmentsPanel.sliderWhites")}
				value={adjustments["whites"]}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "whites"}
				onMouseDown={() => setShowingFloatingMaskingPanel(false)}
				onMouseUp={() => setShowingFloatingMaskingPanel(true)}
				onUpdate={(val) => handleUpdate("whites", val)}
				onUpdateEnded={(_, displayedValue) => handleUpdateEnded("Whites", displayedValue)}
			/>
			<Slider
				title={t("maskAdjustmentsPanel.sliderBlacks")}
				value={adjustments["blacks"]}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "blacks"}
				onMouseDown={() => setShowingFloatingMaskingPanel(false)}
				onMouseUp={() => setShowingFloatingMaskingPanel(true)}
				onUpdate={(val) => handleUpdate("blacks", val)}
				onUpdateEnded={(_, displayedValue) => handleUpdateEnded("Blacks", displayedValue)}
			/>
			<Sep />
			<Slider
				title={t("maskAdjustmentsPanel.sliderTemp")}
				background={sliderBackgrounds.temperature}
				value={adjustments["temperature"]}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "temperature"}
				onMouseDown={() => setShowingFloatingMaskingPanel(false)}
				onMouseUp={() => setShowingFloatingMaskingPanel(true)}
				onUpdate={(val) => handleUpdate("temperature", val)}
				onUpdateEnded={(_, displayedValue) =>
					handleUpdateEnded("Temperature", displayedValue)
				}
			/>
			<Slider
				title={t("maskAdjustmentsPanel.sliderTint")}
				background={sliderBackgrounds.tint}
				value={adjustments["tint"]}
				transform={noopSliderTransform}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "tint"}
				onMouseDown={() => setShowingFloatingMaskingPanel(false)}
				onMouseUp={() => setShowingFloatingMaskingPanel(true)}
				onUpdate={(val) => handleUpdate("tint", val)}
				onUpdateEnded={(_, displayedValue) => handleUpdateEnded("Tint", displayedValue)}
			/>
			<Slider
				title={t("maskAdjustmentsPanel.sliderSaturation")}
				disabled={disablesChromaControls}
				background={sliderBackgrounds.saturation}
				value={adjustments["saturation"]}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "saturation"}
				onMouseDown={() => setShowingFloatingMaskingPanel(false)}
				onMouseUp={() => setShowingFloatingMaskingPanel(true)}
				onUpdate={(val) => handleUpdate("saturation", val)}
				onUpdateEnded={(_, displayedValue) =>
					handleUpdateEnded("Saturation", displayedValue)
				}
			/>
			<Sep />
			<Slider
				title={t("maskAdjustmentsPanel.sliderTexture")}
				value={adjustments["texture"]}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "texture"}
				onMouseDown={() => setShowingFloatingMaskingPanel(false)}
				onMouseUp={() => setShowingFloatingMaskingPanel(true)}
				onUpdate={(val) => handleUpdate("texture", val)}
				onUpdateEnded={(_, displayedValue) => handleUpdateEnded("Texture", displayedValue)}
			/>
			<Slider
				title={t("maskAdjustmentsPanel.sliderClarity")}
				value={adjustments["clarity"]}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "clarity"}
				onMouseDown={() => setShowingFloatingMaskingPanel(false)}
				onMouseUp={() => setShowingFloatingMaskingPanel(true)}
				onUpdate={(val) => handleUpdate("clarity", val)}
				onUpdateEnded={(_, displayedValue) => handleUpdateEnded("Clarity", displayedValue)}
			/>
			<Slider
				title={t("maskAdjustmentsPanel.sliderDehaze")}
				value={adjustments["dehaze"]}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "dehaze"}
				onMouseDown={() => setShowingFloatingMaskingPanel(false)}
				onMouseUp={() => setShowingFloatingMaskingPanel(true)}
				onUpdate={(val) => handleUpdate("dehaze", val)}
				onUpdateEnded={(_, displayedValue) => handleUpdateEnded("Dehaze", displayedValue)}
			/>
			<Sep />
			<Slider
				title={t("maskAdjustmentsPanel.sliderSharpness")}
				value={adjustments["sharpen"]}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "sharpen"}
				onMouseDown={() => setShowingFloatingMaskingPanel(false)}
				onMouseUp={() => setShowingFloatingMaskingPanel(true)}
				onUpdate={(val) => handleUpdate("sharpen", val)}
				onUpdateEnded={(_, displayedValue) =>
					handleUpdateEnded("Sharpness", displayedValue)
				}
			/>
			<Sep />
			{!disablesChromaControls && (
				<HSLPointColor
					isForMask={true}
					values={adjustments}
					onSliderDown={() => setShowingFloatingMaskingPanel(false)}
					onSliderUp={() => setShowingFloatingMaskingPanel(true)}
					onAddColor={(pointColor) => {
						updateMaskGroup(selectedGroup, {
							pointColors: [...adjustments.pointColors, pointColor],
						})

						saveCurrentAdjustmentState({ message: "Added Point Color Swatch" })
					}}
					onChangeColor={(pointColor) => {
						const selectedIndex = adjustments.pointColors.findIndex(
							(value) => value.id === pointColor.id,
						)

						if (selectedIndex < 0) {
							return
						}

						const pointColors = adjustments.pointColors
						pointColors[selectedIndex] = pointColor

						updateMaskGroup(selectedGroup, {
							pointColors,
						})
					}}
					onEndChangingColor={(_, name, displayValue) => {
						saveCurrentAdjustmentState({ message: name, changedValue: displayValue })
					}}
					onRemoveColor={(pointColor) => {
						updateMaskGroup(selectedGroup, {
							pointColors: adjustments.pointColors.filter(
								(color) => color.id !== pointColor.id,
							),
						})

						saveCurrentAdjustmentState({ message: "Removed Point Color Swatch" })
					}}
					onRemoveAllColors={() => {
						updateMaskGroup(selectedGroup, {
							pointColors: [],
						})

						saveCurrentAdjustmentState({ message: "Removed All Point Color Swatches" })
					}}
				/>
			)}
		</div>
	)
}

const MaskSettingsPanel = (props: { onSliderUp: () => void; onSliderDown: () => void }) => {
	const selectedMask = useAtomValue(selectedMaskAtom)
	const selectedGroup = useAtomValue(selectedMaskGroupAtom)

	if (!selectedMask || !selectedGroup) {
		return null
	}

	const mask = selectedMask.properties

	const handleUpdate = (name: keyof Mask, val: number | boolean) => {
		if (!selectedGroup.properties.active) {
			updateMaskGroup(selectedGroup, { active: true })
		}
		updateMask(selectedMask, { [name]: val, active: true })
	}

	const handleUpdateEnded = async (name: string, val?: number | string) => {
		await saveCurrentAdjustmentState({
			message: `${mask.name}: ${name}`,
			changedValue: val?.toString(),
		})
	}

	return (
		<div>
			<MaskSettings
				group={selectedGroup.properties}
				mask={mask}
				onSliderDown={props.onSliderDown}
				onSliderUp={props.onSliderUp}
				onUpdate={handleUpdate}
				onUpdateEnded={handleUpdateEnded}
			/>
			<Sep />
		</div>
	)
}

interface MaskSettingsProps<T> {
	group: MaskGroup
	mask: T
	onSliderUp: () => void
	onSliderDown: () => void
	onUpdate: (name: keyof T, val: number | boolean) => void
	onUpdateEnded: (name: string, val?: number | string) => void
}

const MaskSettings = (props: MaskSettingsProps<Mask>) => {
	switch (props.mask.type) {
		case "radial":
			return <RadialMaskSettings {...(props as MaskSettingsProps<RadialMask>)} />

		case "gradient":
			return <GradientMaskSettings {...(props as MaskSettingsProps<GradientMask>)} />

		case "background":
		case "subject":
		case "people":
		case "sky":
			return <ObjectMaskSettings {...(props as MaskSettingsProps<ObjectMask>)} />

		case "brush":
			return <BrushMaskSettings {...(props as MaskSettingsProps<BrushMask>)} />

		default:
			return null
	}
}

const RadialMaskSettings = (props: MaskSettingsProps<RadialMask>) => {
	const { t } = useTranslation("components")
	const focusedMaskAdjustment = useAtomValue(focusedMaskAdjustmentAtom)
	const mask = props.mask

	return (
		<>
			<MaskSettingsHeader {...props} />
			<Sep />
			<Slider
				title={t("maskAdjustmentsPanel.sliderFeather")}
				value={mask.feather}
				min={0}
				defaultValue={0.5}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "feather"}
				onMouseUp={props.onSliderUp}
				onMouseDown={props.onSliderDown}
				onUpdate={(val) => props.onUpdate("feather", val)}
				onUpdateEnded={(_, displayedValue) =>
					props.onUpdateEnded("Feather", displayedValue)
				}
			/>
		</>
	)
}

const GradientMaskSettings = (props: MaskSettingsProps<GradientMask>) => {
	return <MaskSettingsHeader {...props} />
}

type ObjectMask = SubjectMask | BackgroundMask | PeopleMask | SkyMask

const ObjectMaskSettings = (props: MaskSettingsProps<ObjectMask>) => {
	return <MaskSettingsHeader {...props} />
}

const BrushMaskSettings = (props: MaskSettingsProps<BrushMask>) => {
	const { t } = useTranslation("components")
	const [cursor, setCursor] = useAtom(cursorAtom)
	const focusedMaskAdjustment = useAtomValue(focusedMaskAdjustmentAtom)
	const [currentCursorPresetName, setCurrentCursorPresetName] = useAtom(
		currentCursorPresetNameAtom,
	)

	useEffect(() => {
		const lastStroke = props.mask.strokes.at(-1)
		const desiredPreset = lastStroke?.isErasing ? "erase" : "brush"
		setCurrentCursorPresetName(desiredPreset)
	}, [props.mask.id])

	const onUpdateHandler = (name, val) => {
		props.onUpdate(name, val)
		setCursor({
			...cursor,
			[name]: val,
		})
	}

	const onUpdateBrushHandler = (name) => {
		setCurrentCursorPresetName(name)
	}

	return (
		<>
			<MaskSettingsHeader {...props} />
			<Sep />
			<div className="mb-2 flex gap-2 pl-2">
				<Button
					size="sm"
					className="border-none px-0.5 py-0"
					trackingLabel="select_brush_add"
					aria-label={t("maskAdjustmentsPanel.ariaSwitchToMaskAddBrush")}
					onClick={() => onUpdateBrushHandler("brush")}
					isSelected={currentCursorPresetName === "brush"}
				>
					<Icon name="brush_mask" size={22} />
				</Button>
				<Button
					size="sm"
					className="border-none px-0.5 py-0"
					trackingLabel="select_brush_remove"
					aria-label={t("maskAdjustmentsPanel.ariaSwitchToMaskEraseBrush")}
					onClick={() => onUpdateBrushHandler("erase")}
					isSelected={currentCursorPresetName === "erase"}
				>
					<Icon name="brush_erase_mask" size={22} />
				</Button>
			</div>
			<Slider
				title={t("maskAdjustmentsPanel.sliderSize")}
				value={cursor.radius}
				min={1}
				defaultValue={0.5}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "radius"}
				onMouseUp={props.onSliderUp}
				onMouseDown={props.onSliderDown}
				onUpdate={(val) => onUpdateHandler("radius", val)}
				onUpdateEnded={(_, displayedValue) => props.onUpdateEnded("Radius", displayedValue)}
			/>
			<Slider
				title={t("maskAdjustmentsPanel.sliderFeather")}
				value={cursor.feather}
				min={0}
				defaultValue={0.0}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "feather"}
				onMouseUp={props.onSliderUp}
				onMouseDown={props.onSliderDown}
				onUpdate={(val) => onUpdateHandler("feather", val)}
				onUpdateEnded={(_, displayedValue) =>
					props.onUpdateEnded("Feather", displayedValue)
				}
			/>
			<Slider
				title={t("maskAdjustmentsPanel.sliderFlow")}
				value={cursor.flow}
				min={0}
				defaultValue={0.5}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "flow"}
				onMouseUp={props.onSliderUp}
				onMouseDown={props.onSliderDown}
				onUpdate={(val) => onUpdateHandler("flow", val)}
				onUpdateEnded={(_, displayedValue) => props.onUpdateEnded("Flow", displayedValue)}
			/>
			<Slider
				title={t("maskAdjustmentsPanel.sliderDensity")}
				value={cursor.density}
				min={0}
				defaultValue={1.0}
				keyboardStep={1}
				shiftKeyboardStep={10}
				inFocus={focusedMaskAdjustment === "density"}
				onMouseUp={props.onSliderUp}
				onMouseDown={props.onSliderDown}
				onUpdate={(val) => onUpdateHandler("density", val)}
				onUpdateEnded={(_, displayedValue) =>
					props.onUpdateEnded("Density", displayedValue)
				}
			/>
			<div className="mb-2 flex flex-row items-center gap-2 space-x-2 pl-4 text-xs">
				{t("maskAdjustmentsPanel.autoMask")}
				<Switch
					trackingLabel="brush_cursor_color_aware"
					aria-label={t("maskAdjustmentsPanel.ariaToggleMaskColorAwareBrush")}
					checked={cursor.isColorAware}
					disabled={cursor.isErasing}
					onCheckedChange={(checked) => {
						onUpdateHandler("isColorAware", checked)
					}}
				/>
			</div>
		</>
	)
}

const MaskSettingsHeader = (props: MaskSettingsProps<Mask>) => {
	return (
		<div className="mb-2 flex flex-col space-y-1">
			<div className="flex items-center justify-between pl-2 text-sm font-semibold">
				<div>{props.group.name}</div>
				<MaskInvertToggle {...props} />
			</div>

			<MaskStrengthSlider
				group={props.group}
				onSliderDown={props.onSliderDown}
				onSliderUp={props.onSliderUp}
			/>
		</div>
	)
}

const MaskStrengthSlider = (props: {
	group: MaskGroup
	onSliderUp: () => void
	onSliderDown: () => void
}) => {
	const { t } = useTranslation("components")
	const selectedGroup = useAtomValue(selectedMaskGroupAtom)
	const activeAdjustments = useAtomValue(activeAdjustmentsAtom)
	const focusedMaskAdjustment = useAtomValue(focusedMaskAdjustmentAtom)

	const handleUpdateStrength = (val: number) => {
		if (!selectedGroup) {
			return
		}
		updateMaskGroup(selectedGroup, { strength: val, active: true })
	}

	const handleUpdateStrengthEnded = (val?: number) => {
		if (!selectedGroup) {
			return
		}
		saveCurrentAdjustmentState({
			message: `${selectedGroup.properties.name}: Strength`,
			changedValue: val?.toString(),
		})
	}

	return (
		<Slider
			disabled={!selectedGroup || !activeAdjustments.maskGroups.has(props.group.id)}
			title={t("maskAdjustmentsPanel.sliderStrength")}
			value={props.group.strength}
			min={0}
			max={200}
			defaultValue={1}
			keyboardStep={1}
			shiftKeyboardStep={5}
			hoverKeyboardStep={1}
			hoverShiftKeyboardStep={5}
			inFocus={focusedMaskAdjustment === "strength"}
			onMouseDown={props.onSliderDown}
			onMouseUp={props.onSliderUp}
			onUpdate={(val) => handleUpdateStrength(val)}
			onUpdateEnded={(_, displayedValue) => handleUpdateStrengthEnded(displayedValue)}
		/>
	)
}

const MaskInvertToggle = (props: MaskSettingsProps<Mask>) => {
	const { t } = useTranslation("components")
	const toggleInvertMask = () => {
		props.onUpdate("inverted", !props.mask.inverted)
		props.onUpdateEnded("Invert")
	}

	useShortcuts({
		keys: [actionShortcuts.invertMask],
		action: toggleInvertMask,
		trackingLabel: "invert_mask",
	})

	return (
		<Button
			trackingLabel="invert_mask"
			aria-label={t("maskAdjustmentsPanel.ariaInvertMask")}
			size="sm"
			className="border-none px-0.5 py-0"
			onClick={toggleInvertMask}
			isSelected={props.mask.inverted}
			selectedClass="bg-base-200 text-base-700 hover:bg-base-100 dark:bg-base-300 dark:text-base-800 dark:hover:bg-base-300"
		>
			<Icon name="invert_mask" size={22} />{" "}
			<span className="px-1">{t("maskAdjustmentsPanel.invert")}</span>
		</Button>
	)
}

const Sep = () => <div className="my-2 border-b border-base-900" />
